import * as THREE from 'three';

// World ASSETS (props): barrels, crates, a toolbox and a recycler grinder. Each is a small procedural
// low-poly model built from primitives (matching the rocks/forage style), origin at the BASE centre so it
// sits on the ground. buildAssetModel() returns a fresh THREE.Group; the /admin LOD tool renders these so
// each can be framed + captured as a billboard, the same as the tree/node/terrain assets.
//
// Three barrel colours (owner 2026-07-09) have real GLB replacements for the STANDALONE scattered barrel
// (barrelBlue/Red/Dark) -- see assetField.ts's bakeGLBAssetGeo. makeBarrel()/buildAssetModel() here stay
// pure procedural on purpose: junk piles (makeJunk1 etc.) composite a barrel + crates into ONE merged
// vertex-coloured mesh, and texture-sampling a sub-part of a composite bake is a bigger job than swapping
// the standalone archetype's own geometry -- scoped out for now.

export type AssetId =
  | 'barrelBlue' | 'barrelRed' | 'barrelGrey' | 'barrelDark'
  | 'crateLarge' | 'crateMilitary' | 'crateLocked'
  | 'toolbox' | 'recycler'
  | 'junk1' | 'junk2' | 'junk3' | 'junk4' | 'junk5' | 'junk6';

export const ASSET_LIST: { id: AssetId; label: string }[] = [
  { id: 'barrelBlue', label: 'Blue barrel' },
  { id: 'barrelRed', label: 'Red barrel' },
  { id: 'barrelGrey', label: 'Grey barrel' },
  { id: 'barrelDark', label: 'Dark barrel' },
  { id: 'crateLarge', label: 'Large crate' },
  { id: 'crateMilitary', label: 'Military crate' },
  { id: 'crateLocked', label: 'Locked crate' },
  { id: 'toolbox', label: 'Toolbox' },
  { id: 'recycler', label: 'Recycle grinder' },
  { id: 'junk1', label: 'Junk: 2 barrels + crate' },
  { id: 'junk2', label: 'Junk: barrel + 2 crates' },
  { id: 'junk3', label: 'Junk: crate + toolbox + barrel' },
  { id: 'junk4', label: 'Junk: 2 barrels + toolbox' },
  { id: 'junk5', label: 'Junk: 3 crates' },
  { id: 'junk6', label: 'Junk: barrel + toolbox + debris' },
];

// --- material + primitive helpers -------------------------------------------------------------------
function mat(color: number, roughness = 0.7, metalness = 0.25): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({ color, roughness, metalness, flatShading: true });
}
const METAL_DARK = () => mat(0x2a2d31, 0.5, 0.72); // clamped to the weathered 0.55-0.75 band (MODELING.md)
const METAL_MID = () => mat(0x575b61, 0.55, 0.7);
const WOOD = (c: number) => mat(c, 0.88, 0.04);

function box(g: THREE.Group, w: number, h: number, d: number, x: number, y: number, z: number, material: THREE.Material): void {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), material);
  m.position.set(x, y, z); m.castShadow = true; m.receiveShadow = true; g.add(m);
}
function cyl(g: THREE.Group, rTop: number, rBot: number, h: number, x: number, y: number, z: number, material: THREE.Material, seg = 18): void {
  const m = new THREE.Mesh(new THREE.CylinderGeometry(rTop, rBot, h, seg), material);
  m.position.set(x, y, z); m.castShadow = true; m.receiveShadow = true; g.add(m);
}

// --- models -----------------------------------------------------------------------------------------
// Classic oil drum: ribbed steel body in the paint colour, dark rolled rims top + bottom, a lid with a bung.
function makeBarrel(bodyHex: number): THREE.Group {
  const g = new THREE.Group();
  const T = 1.2; // ~20% taller than the old short drum (heights + Y positions scale; radius unchanged)
  const body = mat(bodyHex, 0.5, 0.55);
  const rib = mat(bodyHex, 0.45, 0.6);
  cyl(g, 0.29, 0.29, 0.86 * T, 0, 0.45 * T, 0, body);                 // body
  cyl(g, 0.305, 0.305, 0.06 * T, 0, 0.06 * T, 0, METAL_DARK());        // bottom rim
  cyl(g, 0.305, 0.305, 0.06 * T, 0, 0.84 * T, 0, METAL_DARK());        // top rim
  cyl(g, 0.30, 0.30, 0.05 * T, 0, 0.32 * T, 0, rib);                   // rolling rib
  cyl(g, 0.30, 0.30, 0.05 * T, 0, 0.58 * T, 0, rib);                   // rolling rib
  cyl(g, 0.285, 0.285, 0.04 * T, 0, 0.88 * T, 0, mat(bodyHex, 0.45, 0.6)); // lid
  cyl(g, 0.055, 0.055, 0.04 * T, 0.16, 0.91 * T, 0, METAL_DARK(), 8);  // bung
  return g;
}

// Big wooden shipping crate: plank body with darker corner posts + top/bottom rails and a band.
function makeCrateLarge(): THREE.Group {
  const g = new THREE.Group();
  const W = 1.1, H = 1.0, D = 1.1;
  box(g, W, H, D, 0, H / 2 + 0.02, 0, WOOD(0x8a6a3f));          // body
  const frame = WOOD(0x5f4827);
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) box(g, 0.1, H + 0.06, 0.1, sx * (W / 2 - 0.02), H / 2 + 0.02, sz * (D / 2 - 0.02), frame); // corner posts
  for (const yy of [0.1, H - 0.06]) { box(g, W + 0.02, 0.09, 0.06, 0, yy, D / 2, frame); box(g, W + 0.02, 0.09, 0.06, 0, yy, -D / 2, frame); box(g, 0.06, 0.09, D + 0.02, W / 2, yy, 0, frame); box(g, 0.06, 0.09, D + 0.02, -W / 2, yy, 0, frame); } // rails
  box(g, W + 0.02, 0.08, 0.05, 0, H / 2, D / 2, frame); box(g, W + 0.02, 0.08, 0.05, 0, H / 2, -D / 2, frame); // mid band
  return g;
}

// Military ammo crate: low olive box, a lid lip, front latches, end handles.
function makeCrateMilitary(): THREE.Group {
  const g = new THREE.Group();
  const W = 1.0, H = 0.44, D = 0.56;
  box(g, W, H, D, 0, H / 2 + 0.02, 0, mat(0x4a5234, 0.7, 0.25));   // body (olive)
  box(g, W + 0.04, 0.1, D + 0.04, 0, H + 0.02, 0, mat(0x3c4429, 0.7, 0.25)); // lid lip
  for (const sx of [-0.28, 0.28]) box(g, 0.09, 0.12, 0.04, sx, H * 0.55, D / 2 + 0.01, METAL_DARK()); // front latches
  for (const sx of [-1, 1]) box(g, 0.05, 0.06, 0.16, sx * (W / 2 + 0.02), H * 0.6, 0, METAL_MID());   // end handles
  box(g, 0.5, 0.02, 0.03, 0, H * 0.45, D / 2 + 0.02, mat(0xb9c08a, 0.8, 0.05)); // stencil stripe
  return g;
}

// Locked crate: reinforced wooden box, dark steel bands wrapping it, corner brackets, a padlock on the front.
function makeCrateLocked(): THREE.Group {
  const g = new THREE.Group();
  const W = 0.9, H = 0.9, D = 0.9;
  box(g, W, H, D, 0, H / 2 + 0.02, 0, WOOD(0x6e5638));           // body
  const band = METAL_DARK();
  for (const yy of [0.28, 0.66]) { box(g, W + 0.03, 0.12, 0.05, 0, yy, D / 2, band); box(g, W + 0.03, 0.12, 0.05, 0, yy, -D / 2, band); box(g, 0.05, 0.12, D + 0.03, W / 2, yy, 0, band); box(g, 0.05, 0.12, D + 0.03, -W / 2, yy, 0, band); }
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) box(g, 0.1, 0.1, 0.1, sx * (W / 2 - 0.01), H - 0.06, sz * (D / 2 - 0.01), band); // top corner brackets
  // padlock on the front
  box(g, 0.14, 0.16, 0.06, 0, 0.47, D / 2 + 0.05, METAL_MID());
  const shackle = new THREE.Mesh(new THREE.TorusGeometry(0.05, 0.018, 8, 12, Math.PI), METAL_MID());
  shackle.position.set(0, 0.56, D / 2 + 0.05); shackle.castShadow = true; g.add(shackle);
  return g;
}

// Small red toolbox: body, lid, a steel carry handle over the top, a front latch.
function makeToolbox(): THREE.Group {
  const g = new THREE.Group();
  const W = 0.5, H = 0.24, D = 0.26;
  box(g, W, H, D, 0, H / 2 + 0.02, 0, mat(0xb23a2a, 0.55, 0.3));   // body
  box(g, W + 0.02, 0.06, D + 0.02, 0, H + 0.01, 0, mat(0x8f2c20, 0.55, 0.3)); // lid
  const hMat = METAL_MID(); // carry handle
  for (const sx of [-0.14, 0.14]) box(g, 0.03, 0.09, 0.03, sx, H + 0.06, 0, hMat);
  box(g, 0.34, 0.03, 0.03, 0, H + 0.11, 0, hMat);
  box(g, 0.07, 0.05, 0.03, 0, H * 0.55, D / 2 + 0.01, METAL_DARK()); // front latch
  return g;
}

// Recycle grinder: bench-height green cabinet with yellow hazard trim, an open top hopper, and two toothed
// grinder rollers in the mouth (where items drop in to be broken into parts). Green body + yellow accents.
function makeRecycler(): THREE.Group {
  const g = new THREE.Group();
  const W = 1.0, H = 0.9, D = 0.62;
  const green = mat(0x2f7d3a, 0.7, 0.35);
  const yellow = mat(0xe3c024, 0.6, 0.3);
  box(g, W, H, D, 0, H / 2 + 0.02, 0, green);                    // cabinet body
  box(g, W + 0.03, 0.1, D + 0.03, 0, H - 0.06, 0, yellow);       // yellow top band
  box(g, W + 0.03, 0.08, D + 0.03, 0, 0.08, 0, yellow);          // yellow kick base
  box(g, 0.34, 0.2, 0.04, -0.22, H * 0.5, D / 2 + 0.02, yellow); // front control plate
  for (const sx of [-0.27, -0.17]) box(g, 0.03, 0.03, 0.02, sx, H * 0.5, D / 2 + 0.04, METAL_DARK()); // buttons
  box(g, 0.72, 0.12, 0.42, 0, H + 0.02, 0, mat(0x141414, 0.9, 0.1)); // hopper mouth (recessed dark opening)
  // two toothed grinder rollers lying across the mouth (cylinders + ring teeth)
  for (const zz of [-0.1, 0.1]) {
    const roller = new THREE.Mesh(new THREE.CylinderGeometry(0.11, 0.11, 0.7, 10), METAL_MID());
    roller.rotation.z = Math.PI / 2; roller.position.set(0, H + 0.05, zz); roller.castShadow = true; g.add(roller);
    for (let t = 0; t < 8; t++) { // teeth
      const tooth = new THREE.Mesh(new THREE.BoxGeometry(0.66, 0.03, 0.03), METAL_DARK());
      tooth.rotation.x = (t / 8) * Math.PI * 2; tooth.position.set(0, H + 0.05, zz); g.add(tooth);
    }
    cyl(g, 0.12, 0.12, 0.04, 0.35, H + 0.05, zz, yellow, 10); // yellow end caps
    cyl(g, 0.12, 0.12, 0.04, -0.35, H + 0.05, zz, yellow, 10);
  }
  return g;
}

// --- junk piles: loose clusters of the base props (proto-monument loot pockets) -----------------------
function place(g: THREE.Group, child: THREE.Group, x: number, z: number, rotY: number, y = 0, scale = 1): void {
  child.position.set(x, y, z); child.rotation.y = rotY; child.scale.setScalar(scale); g.add(child);
}
function addPlanks(g: THREE.Group): void { // scattered flat planks for a debris look
  const w = WOOD(0x7a5e34);
  for (const [x, z, r] of [[-0.12, 0.5, 0.4], [0.16, 0.58, 1.1], [0.0, 0.66, -0.3]] as const) {
    const m = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.05, 0.13), w);
    m.position.set(x, 0.03, z); m.rotation.y = r; m.castShadow = true; m.receiveShadow = true; g.add(m);
  }
}
function makeJunk1(): THREE.Group { const g = new THREE.Group(); place(g, makeBarrel(0x2f5fa8), -0.55, 0.18, 0.3); place(g, makeBarrel(0xa83232), 0.12, -0.5, -0.6); place(g, makeCrateLarge(), 0.55, 0.5, 0.25, 0, 0.8); return g; }
function makeJunk2(): THREE.Group { const g = new THREE.Group(); place(g, makeBarrel(0x8a8f96), -0.6, 0.1, 0.5); place(g, makeCrateLarge(), 0.28, 0.42, -0.2, 0, 0.85); place(g, makeCrateMilitary(), 0.5, -0.5, 0.6); return g; }
function makeJunk3(): THREE.Group { const g = new THREE.Group(); place(g, makeCrateLarge(), -0.05, 0.0, 0.15, 0, 0.9); place(g, makeToolbox(), -0.05, 0.0, 0.5, 0.92); place(g, makeBarrel(0x2b2e33), 0.78, 0.22, -0.4); return g; }
function makeJunk4(): THREE.Group { const g = new THREE.Group(); place(g, makeBarrel(0x2f5fa8), -0.42, 0.2, 0.3); place(g, makeBarrel(0x8a8f96), 0.38, -0.18, -0.5); place(g, makeToolbox(), 0.05, 0.6, 0.8); return g; }
function makeJunk5(): THREE.Group { const g = new THREE.Group(); place(g, makeCrateLarge(), 0.0, 0.0, 0.1, 0, 0.9); place(g, makeCrateMilitary(), 0.72, 0.32, -0.4); place(g, makeCrateLocked(), -0.55, 0.5, 0.5, 0, 0.75); return g; }
function makeJunk6(): THREE.Group { const g = new THREE.Group(); place(g, makeBarrel(0xa83232), -0.32, 0.2, 0.4); place(g, makeToolbox(), 0.42, -0.3, 0.9); addPlanks(g); return g; }

// Build a fresh model group for an asset id (origin at base centre, sitting on y=0).
export function buildAssetModel(id: AssetId): THREE.Group {
  switch (id) {
    case 'barrelBlue': return makeBarrel(0x2f5fa8);
    case 'barrelRed': return makeBarrel(0xa83232);
    case 'barrelGrey': return makeBarrel(0x8a8f96);
    case 'barrelDark': return makeBarrel(0x2b2e33);
    case 'crateLarge': return makeCrateLarge();
    case 'crateMilitary': return makeCrateMilitary();
    case 'crateLocked': return makeCrateLocked();
    case 'toolbox': return makeToolbox();
    case 'recycler': return makeRecycler();
    case 'junk1': return makeJunk1();
    case 'junk2': return makeJunk2();
    case 'junk3': return makeJunk3();
    case 'junk4': return makeJunk4();
    case 'junk5': return makeJunk5();
    case 'junk6': return makeJunk6();
  }
}
